import React, { useState, useEffect } from 'react'
import validate from 'validate.js'
import { makeStyles } from '@material-ui/styles'
import { colors } from '@material-ui/core'
import { useUsuarioService } from 'hooks/useUsuarioService'
import useRouter from 'utils/useRouter'
import { Page, Header, CustomButton } from 'components'
import { Formulario } from './components'
import { useAuth } from 'hooks/useAuth'
import constants from 'utils/constants'
import { ModalConfirmarAcao } from 'views/ExamesSolicitados/ExameSolicitado/components/MenuAcoes/components/ModalConfirmarAcao'

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: '100%',
    margin: '0 auto',
    padding: theme.spacing(3),
  },
  actions: {
    marginTop: theme.spacing(3),
  },
  deleteButton: {
    marginLeft: 10,
    background: `linear-gradient(45deg, ${colors.red[900]} 30%, ${colors.red[800]} 90%)`,
    color: 'white',
  },
  buttonProgress: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
}))

const initialForm = {
  nome: '',
  cpf: '',
  email: '',
  matricula: '',
  cargo: null,
  setores: [],
  permissoesNegadas: [],
  permissoesAdicionais: [],
  notificacao: false,
}

const schema = {
  nome: { presence: { allowEmpty: false, message: '^Nome é obrigatório' } },
  cpf: {
    format: { pattern: '[0-9]{3}[.][0-9]{3}[.][0-9]{3}[-][0-9]{2}', message: '^CPF inválido' },
    presence: { allowEmpty: false, message: '^CPF é obrigatório' },
  },
  email: { email: { message: '^Email inválido' }, presence: { allowEmpty: false, message: '^E-mail é obrigatório' } },
  cargo: { presence: { allowEmpty: false, message: '^Cargo é obrigatório' } },
}

const validateCPF = (number) => {
  const cpf = number.replace(/[/.-]/g, '')

  if (cpf.length !== 11 || /^([0-9])\1+$/.test(cpf)) {
    return false
  }

  let sum = 0
  let remainder

  for (let index = 1; index <= 9; index += 1) {
    sum += parseInt(cpf.substring(index - 1, index), 10) * (11 - index)
  }

  remainder = (sum * 10) % 11
  if (remainder === 10 || remainder === 11) {
    remainder = 0
  }
  if (remainder !== parseInt(cpf.substring(9, 10), 10)) {
    return false
  }

  sum = 0
  for (let index = 1; index <= 10; index += 1) {
    sum += parseInt(cpf.substring(index - 1, index), 10) * (12 - index)
  }

  remainder = (sum * 10) % 11
  if (remainder === 10 || remainder === 11) {
    remainder = 0
  }

  return remainder === parseInt(cpf.substring(10, 11), 10)
}

export const EditarUsuario = (props) => {
  const router = useRouter()
  const usuarioService = useUsuarioService()
  const { route } = props

  const classes = useStyles()
  const [dados, setDados] = useState([])
  const [form, setForm] = useState(initialForm)
  const [touched, setTouched] = useState({})
  const [errors, setErrors] = useState({})
  const [processando, setProcessando] = useState(false)
  const [openAlertDelete, setOpenAlertDelete] = useState(false)
  const { permissoesUsuario, verificarPermissao } = useAuth()

  useEffect(() => {
    if (route.editar && dados.id) {
      setForm({
        ...initialForm,
        nome: dados.nome || '',
        cpf: dados.cpf || '',
        email: dados.email || '',
        matricula: dados.matricula || '',
        cargo: dados.cargo || null,
        setores: dados.setores || [],
        permissoesNegadas: dados.permNegs || [],
        permissoesAdicionais: dados.permAdics || [],
      })
    }
  }, [dados, route.editar])

  useEffect(() => {
    const validationErrors = validate(form, schema) || {}

    if (form.cpf && !validateCPF(form.cpf)) {
      validationErrors.cpf = ['Digite um CPF válido']
    }

    setErrors(validationErrors)
  }, [form])

  const cpfMask = (value) => value.replace(/\D/g, '').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})/, '$1-$2').replace(/(-\d{2})\d+?$/, '$1')

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target
    const nextValue = name === 'cpf' ? cpfMask(value) : type === 'checkbox' ? checked : value
    setForm((previous) => ({ ...previous, [name]: nextValue }))
    setTouched((previous) => ({ ...previous, [name]: true }))
  }

  const changeForm = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }))
    setTouched((previous) => ({ ...previous, [field]: true }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    setTouched(Object.keys(form).reduce((fields, field) => ({ ...fields, [field]: true }), {}))
    const validationErrors = validate(form, schema) || {}
    if (form.cpf && !validateCPF(form.cpf)) {
      validationErrors.cpf = ['Digite um CPF válido']
    }
    if (Object.keys(validationErrors).length) return

    setProcessando(true)
    if (route.editar) await usuarioService.editarUsuario(dados.id, form)
    else await usuarioService.cadastrarUsuario(form)
    setProcessando(false)
    router.history.push(router.location.pathname.replace('/cadastrar', '').replace(/\/editar\/\d+/, ''))
  }

  const handleClickOpenAlertDelete = () => {
    setOpenAlertDelete(true)
  }

  const handleCloseAlertDelete = () => {
    setOpenAlertDelete(false)
  }

  const rotaAdmin = router.location.pathname.startsWith('/usuarios')
  useEffect(() => {
    if (permissoesUsuario?.length) {
      if ((rotaAdmin && !verificarPermissao([constants.PERMISSOES.ADMINISTRAR_SISTEMA]))
      || (!rotaAdmin && !verificarPermissao([constants.PERMISSOES.CADASTRAR_USUARIO]))) {
        router.history.push(`/`)
      }
    }
  }, [permissoesUsuario])

  useEffect(() => {
    const fetchDados = async () => {
      if (rotaAdmin) {
        const user = await usuarioService.listarUsuarioAsync(router.match.params.idUsuario)
        setDados(user)
      } else {
        const user = await usuarioService.listarUsuarioExterno(router.match.params.idUsuario)
        if (!user) {
          router.history.push(`/`)
        }
        setDados(user)
      }
    }
    if (route.editar) {
      fetchDados()
    }
  }, [route.editar, rotaAdmin])

  const handleDelete = async (event) => {
    event.preventDefault()
    await usuarioService.deletarUsuario(dados.id)
    router.history.push('/usuarios')
  }

  return (
    <Page className={classes.root} title={`${route.titulo} - SIGEP`}>
      <Header titulo={route.titulo} overline={route.secao} />       
      <Formulario
        editar={route.editar}
        dados={dados}
        form={form}
        touched={touched}
        errors={errors}
        onChange={handleChange}
        onChangeValue={changeForm}
        onSubmit={handleSubmit}
      />
      <div className={classes.actions}>
        {!route.editar ? (
          <CustomButton
            form="form-edit-usuario"
            type="submit"
            style={{ float: 'right' }}
          >
            Cadastrar usuário
          </CustomButton>
        ) : (
          <>
            <CustomButton
              form="form-edit-usuario"
              type="submit"
              style={{ float: 'right' }}
            >
              Atualizar
            </CustomButton>
            
            {rotaAdmin && 
            (<CustomButton
              onClick={handleClickOpenAlertDelete}
              className={classes.deleteButton}
            >
              Excluir
            </CustomButton>)}
          </>
        )}
      </div>
      <ModalConfirmarAcao 
        aberto={openAlertDelete}
        onClose={handleCloseAlertDelete}
        titulo="Tem certeza que deseja deletar?"
        mensagem={`${dados.nome} não poderá mais ser acessado.`}
        onConfirm={handleDelete}
      />
    </Page>
  )
}
